import { useState, useEffect, useMemo } from "react";
import api from "../../services/api";
import "../../assets/styles/ITAdmin/ITALogsPg.css";

// ── helpers ──
const monthKey  = (d) => { try { const dt=new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch{return null;} };
const dayKey    = (d) => { try { return new Date(d).toISOString().split("T")[0]; } catch{return null;} };
const fmtDate   = (d) => { try { return new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); } catch{return d||"—";} };
const fmtDT     = (d) => { try { const dt=new Date(d); return dt.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); } catch{return d||"—";} };
const monthLabel= (k) => { if(!k)return""; const [y,m]=k.split("-"); return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); };
const todayKey  = () => new Date().toISOString().split("T")[0];

// ── CSV export ──
function exportCSV(rows, filename) {
  const headers = ["Timestamp","Admin","Action","Target","Detail","Date"];
  const lines = [headers.join(",")];
  rows.forEach(l => {
    lines.push([
      l.timestamp_str||"", l.admin_name||"", l.action||"",
      l.target||"", l.detail||"",
      l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN") : ""
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8;"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Action colour chip ──
const actionColor = (action="") => {
  const a = action.toLowerCase();
  if (a.includes("delete")||a.includes("lost")||a.includes("reject"))
    return {bg:"#fef2f2",color:"#dc2626"};
  if (a.includes("add")||a.includes("create")||a.includes("complet"))
    return {bg:"#eff6ff",color:"#2563eb"};
  if (a.includes("update")||a.includes("edit")||a.includes("assign"))
    return {bg:"#fef3c7",color:"#d97706"};
  if (a.includes("send")||a.includes("forward")||a.includes("approv"))
    return {bg:"#ecfdf5",color:"#059669"};
  return {bg:"#f3f4f6",color:"#374151"};
};

function ITALogsPg() {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [dayFilter, setDayFilter] = useState("all");  // "all" | "today" | YYYY-MM-DD
  const [monthF,    setMonthF]    = useState("all");
  const [showMDrop, setShowMDrop] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Use full logs endpoint (not context — context is limited to 10)
    fetch(`${process.env.REACT_APP_API_URL||"/api"}/itadmin/logs`, {
      headers: { Authorization: `Bearer ${(() => {
        const role = sessionStorage.getItem("activeRole");
        if (role) return localStorage.getItem("token_"+role)||"";
        for (const r of ["itadmin","superadmin","admin"]) {
          const t = localStorage.getItem("token_"+r); if (t) return t;
        }
        return "";
      })()}` }
    })
    .then(r=>r.json())
    .then(d=>{ setLogs(Array.isArray(d)?d:[]); setLoading(false); })
    .catch(()=>setLoading(false));
  }, []);

  // ── derived ──
  const availMonths = useMemo(()=>{
    const set=new Set(); logs.forEach(l=>{ const k=monthKey(l.created_at||l.timestamp_str); if(k) set.add(k); });
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  },[logs]);

  const availDays = useMemo(()=>{
    const set=new Set(); logs.forEach(l=>{ const k=dayKey(l.created_at); if(k) set.add(k); });
    return Array.from(set).sort((a,b)=>b.localeCompare(a)).slice(0,30);
  },[logs]);

  const filtered = useMemo(()=>{
    let list = logs;
    // Day filter
    if (dayFilter==="today")
      list = list.filter(l=>dayKey(l.created_at)===todayKey());
    else if (dayFilter!=="all")
      list = list.filter(l=>dayKey(l.created_at)===dayFilter);
    // Month filter
    if (monthF!=="all")
      list = list.filter(l=>monthKey(l.created_at||l.timestamp_str)===monthF);
    // Search
    if (search.trim()) {
      const q=search.toLowerCase();
      list = list.filter(l=>
        (l.admin_name||"").toLowerCase().includes(q)||
        (l.action||"").toLowerCase().includes(q)||
        (l.target||"").toLowerCase().includes(q)||
        (l.detail||"").toLowerCase().includes(q)
      );
    }
    return list;
  },[logs,dayFilter,monthF,search]);

  // stats
  const todayCount = useMemo(()=>logs.filter(l=>dayKey(l.created_at)===todayKey()).length,[logs]);

  const csvName = ()=>{
    const parts=["action_logs"];
    if(dayFilter==="today") parts.push("today_"+todayKey());
    else if(dayFilter!=="all") parts.push(dayFilter);
    if(monthF!=="all") parts.push(monthF);
    return parts.join("_")+".csv";
  };

  return (
    <div>
      <div className="sec">Action Logs</div>

      {/* ── Summary strip ── */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
        {[
          {label:"Total Logs",  val:logs.length,    color:"#4f46e5", icon:"📋"},
          {label:"Today",       val:todayCount,     color:"#059669", icon:"📅"},
          {label:"This Month",  val:useMemo(()=>logs.filter(l=>monthKey(l.created_at||l.timestamp_str)===monthKey(new Date().toISOString())).length,[logs]),
                                                    color:"#d97706", icon:"🗓️"},
        ].map(s=>(
          <div key={s.label} style={{display:"flex",flexDirection:"column",alignItems:"center",
            padding:"10px 18px",borderRadius:12,background:"#f8fafc",
            boxShadow:"0 1px 4px rgba(0,0,0,.06)",minWidth:80}}>
            <span style={{fontSize:17,marginBottom:2}}>{s.icon}</span>
            <span style={{fontSize:22,fontWeight:800,color:s.color,lineHeight:1.1}}>{s.val}</span>
            <span style={{fontSize:10,fontWeight:600,color:"#64748b",marginTop:2}}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>

        {/* Search */}
        <input placeholder="🔍 Search admin, action, target…" value={search}
          onChange={e=>setSearch(e.target.value)}
          style={{padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:8,
            fontSize:12,flex:1,minWidth:180,outline:"none"}} />

        {/* Day filter */}
        <select value={dayFilter} onChange={e=>setDayFilter(e.target.value)}
          style={{padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:8,
            fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
          <option value="all">All Days</option>
          <option value="today">Today</option>
          {availDays.filter(d=>d!==todayKey()).map(d=>(
            <option key={d} value={d}>{fmtDate(d)}</option>
          ))}
        </select>

        {/* Month filter */}
        <select value={monthF} onChange={e=>setMonthF(e.target.value)}
          style={{padding:"8px 12px",border:"1px solid #e2e8f0",borderRadius:8,
            fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
          <option value="all">All Months</option>
          {availMonths.filter(m=>m!=="all").map(m=>(
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>

        {/* Reset */}
        {(dayFilter!=="all"||monthF!=="all"||search) && (
          <button onClick={()=>{setDayFilter("all");setMonthF("all");setSearch("");}}
            style={{padding:"6px 12px",border:"1px solid #fecaca",borderRadius:8,
              background:"#fef2f2",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            ✕ Reset
          </button>
        )}

        {/* Monthly dropdown download */}
        <div style={{position:"relative"}}>
          <button onClick={()=>setShowMDrop(v=>!v)}
            style={{display:"flex",alignItems:"center",gap:5,padding:"8px 14px",
              background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
              fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
            📅 Monthly CSV ▾
          </button>
          {showMDrop && (
            <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#fff",
              border:"1px solid #e2e8f0",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.12)",
              zIndex:200,minWidth:240,overflow:"hidden"}}>
              <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                borderBottom:"1px solid #f1f5f9",background:"#f8fafc"}}>SELECT MONTH</div>
              {/* Today shortcut */}
              <button onClick={()=>{ const today=logs.filter(l=>dayKey(l.created_at)===todayKey()); exportCSV(today,`action_logs_today_${todayKey()}.csv`); setShowMDrop(false); }}
                style={{display:"flex",justifyContent:"space-between",width:"100%",padding:"10px 14px",
                  border:"none",background:"#fff",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <span style={{fontWeight:700,color:"#059669"}}>📅 Today</span>
                <span style={{fontSize:11,color:"#94a3b8"}}>{todayCount} logs</span>
              </button>
              {availMonths.filter(m=>m!=="all").map(m=>{
                const mRows=logs.filter(l=>monthKey(l.created_at||l.timestamp_str)===m);
                return (
                  <button key={m}
                    onClick={()=>{ exportCSV(mRows,`action_logs_${m}.csv`); setShowMDrop(false); }}
                    style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                      cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <span style={{fontWeight:600,color:"#1e293b"}}>{monthLabel(m)}</span>
                    <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} logs</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Download current view */}
        <button onClick={()=>exportCSV(filtered,csvName())}
          style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",
            background:"#0f172a",color:"#fff",border:"none",borderRadius:8,
            fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
          ⬇ Download CSV {filtered.length!==logs.length?`(${filtered.length})`:""}
        </button>
      </div>

      {/* ── Active filter banner ── */}
      {(dayFilter!=="all"||monthF!=="all") && (
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",borderRadius:10,
          padding:"12px 18px",marginBottom:14,display:"flex",justifyContent:"space-between",
          alignItems:"center",color:"#fff"}}>
          <div>
            <div style={{fontWeight:700,fontSize:14}}>
              {dayFilter==="today" ? "📅 Today's Logs"
               : dayFilter!=="all" ? `📅 ${fmtDate(dayFilter)}`
               : `📅 ${monthLabel(monthF)}`}
            </div>
            <div style={{fontSize:11,opacity:.8,marginTop:2}}>{filtered.length} entries</div>
          </div>
          <div style={{display:"flex",gap:12}}>
            {["asset","credential","exit","request","maintenance"].map(type=>{
              const c=filtered.filter(l=>(l.action||"").toLowerCase().includes(type)).length;
              return c>0?(
                <div key={type} style={{textAlign:"center"}}>
                  <div style={{fontSize:18,fontWeight:800}}>{c}</div>
                  <div style={{fontSize:9,opacity:.75,textTransform:"capitalize"}}>{type}</div>
                </div>
              ):null;
            })}
          </div>
          <button onClick={()=>exportCSV(filtered,csvName())}
            style={{padding:"7px 14px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            ⬇ Download CSV
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">IT Admin Activity Trail</div>
          <div className="cd-s">
            {filtered.length} {filtered.length!==logs.length?`of ${logs.length} `:""} entries
          </div>
        </div>
        <div className="cd-b italogspg-inline-1">
          {loading ? (
            <div style={{textAlign:"center",padding:40,color:"#94a3b8"}}>Loading logs…</div>
          ) : filtered.length===0 ? (
            <div style={{textAlign:"center",padding:40,color:"#94a3b8"}}>No logs match the current filter</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Timestamp</th><th>Admin</th><th>Action</th>
                  <th>Target</th><th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l,i)=>{
                  const ac = actionColor(l.action);
                  return (
                    <tr key={i}>
                      <td className="italogspg-inline-2" style={{whiteSpace:"nowrap",fontSize:11}}>
                        {l.timestamp_str || fmtDT(l.created_at)}
                      </td>
                      <td className="italogspg-inline-3" style={{fontWeight:600}}>{l.admin_name||l.admin||"—"}</td>
                      <td>
                        <span style={{background:ac.bg,color:ac.color,
                          padding:"3px 9px",borderRadius:20,fontSize:11,fontWeight:700,
                          whiteSpace:"nowrap"}}>
                          {l.action||"—"}
                        </span>
                      </td>
                      <td style={{fontSize:12,color:"#4f46e5",fontWeight:600}}>{l.target||"—"}</td>
                      <td className="italogspg-inline-4" style={{fontSize:12,color:"#64748b",maxWidth:280,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={l.detail}>
                        {l.detail||"—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default ITALogsPg;
